import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DistressSlider from "../components/DistressSlider";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, AlertTriangle } from "lucide-react";

export default function Checkin() {
  const [value, setValue] = useState(3);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/checkins").then((r) => setHistory(r.data.slice(0, 8))).catch(() => {});
  }, []);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/checkins", { distress: value, note });
      toast.success("Check-in saved");
      const r = await api.get("/checkins");
      setHistory(r.data.slice(0, 8));
      if (value >= 7) {
        // gently route to assistant
        setTimeout(() => nav("/assistant"), 500);
      }
    } catch {
      toast.error("Could not save check-in");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="checkin-page">
      <header>
        <h1 className="font-display text-3xl font-bold text-slate-900">Daily check-in</h1>
        <p className="text-slate-600 mt-2 max-w-xl">Take a moment to notice how you are right now. There is no right answer.</p>
      </header>

      <DistressSlider value={value} onChange={setValue} />

      {value >= 8 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-red-900 flex items-start gap-3" data-testid="checkin-high-warning">
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <div className="text-sm">
            Your distress is high. After saving, we will open your AI companion to help you work through your safety plan. If you are in immediate danger please call <strong>999</strong> or <strong>NHS 111</strong> now.
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <label className="text-sm font-medium text-slate-700">What's going on? (optional)</label>
        <textarea
          data-testid="checkin-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className="mt-2 w-full border border-slate-300 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
          placeholder="A few words to your future self…"
        />
        <button
          data-testid="checkin-submit"
          disabled={saving}
          onClick={submit}
          className="mt-4 w-full md:w-auto bg-teal-700 hover:bg-teal-800 text-white rounded-xl py-3 px-6 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {saving ? "Saving…" : (<>Save check-in <ArrowRight className="w-4 h-4" /></>)}
        </button>
      </div>

      {history.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="checkin-history">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Recent check-ins</h2>
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-semibold text-slate-800">{h.distress}/10</span>
                  {h.note && <span className="ml-3 text-slate-600">{h.note}</span>}
                </div>
                <span className="text-slate-400 text-xs">{new Date(h.created_at).toLocaleString("en-GB")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
